<template>
  <div
    ref="slider"
    class="image-text-overlay-slider"
    :class="[colorScheme]"
  >
    <splide
      ref="splide"
      :has-track="false"
      :options="localConfig"
      @splide:move="handleSplideMove"
      @splide:mounted="handleSplideMounted"
    >
      <div
        v-if="blocks?.length"
        class="splide__arrows-container"
      >
        <div
          ref="splide__arrows"
          class="splide__arrows"
        >
          <button
            type="button"
            :class="[
              blocks[0].tabTitleFontSizeMobile,
              blocks[0].tabTitleFontSizeDesktop,
              blocks[0].tabTitleFontWeightMobile,
              blocks[0].tabTitleFontWeightDesktop,
            ]"
            class="splide__arrow splide__arrow--prev"
            v-text="blocks[0].tabTitle"
          />
          <button
            v-if="blocks?.length > 1"
            type="button"
            :class="[
              blocks[1].tabTitleFontSizeMobile,
              blocks[1].tabTitleFontSizeDesktop,
              blocks[1].tabTitleFontWeightMobile,
              blocks[1].tabTitleFontWeightDesktop,
            ]"
            class="splide__arrow splide__arrow--next"
            v-text="blocks[1].tabTitle"
          />
          <span
            ref="tab_underline"
            class="splide__tab-underline"
          />
        </div>
      </div>
      <splide-track>
        <splide-slide
          v-for="(block, index) of blocksData"
          :key="index"
        >
          <div
            class="image-text-overlay
              critical-component-clear
              liquid"
            :class="setBlockClasses(block)"
            :style="setInlineStyles(block)"
          >
            <div
              :class="[
                'image-text-overlay__text--desktop-' + block.desktopTextPosition,
                'desktop-vertical-' + block.desktopTextPositionVertical,
                'image-text-overlay__text--mobile-' + block.mobileTextPosition,
              ]"
              class="image-text-overlay__column
                image-text-overlay__text
                container"
            >
              <div
                v-if="block.openHours"
                class="
                  image-text-overlay__store-hours-wrapper
                  image-text-overlay__rte
                  rte
                "
              >
                <div
                  v-if="block.openNow"
                  class="image-text-overlay__is-open"
                />
                <p
                  class="image-text-overlay__store-hours"
                  v-text="block.openHours"
                />
              </div>
              <h2
                class="image-text-overlay__title"
                :class="[
                  block.headingFontSizeMobile,
                  block.headingFontSizeDesktop,
                  block.headingFontWeightMobile,
                  block.headingFontWeightDesktop,
                  block.headingFontFamily,
                ]"
              >
                {{ block.heading }}
              </h2>
              <div
                v-if="block.copy && !isMobile"
                class="image-text-overlay__rte rte"
                v-html="block.copy"
              />
              <div
                v-if="block.mobileCopy && isMobile"
                class="image-text-overlay__rte rte"
                v-html="block.mobileCopy"
              />
              <btn
                v-if="block.ctaText && block.ctaUrl"
                class="image-text-overlay__cta button"
                :modifiers="`${block.ctaType} ${block.ctaColor}`"
                :label="block.ctaText"
                type="button"
                :url="block.ctaUrl"
              />
            </div>
          </div>
        </splide-slide>
      </splide-track>
    </splide>
  </div>
</template>

<script>

/**
* Vue: Image text overlay (image-text-overlay)
* -----------------------------------------------------------------------------
* Renders image text overlay blocks as a slider.
*
* @param {Array} blocks - Section blocks.
*/

import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import Btn from '~global/btn/btn'
import { getStoreHours, storeLocatorProps } from '~async/store-locator-map/helpers'

export default {
  name: 'ImageTextOverlay',
  components: {
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
  },
  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      cnvsStatic: !this?.blocks?.length,
      colorScheme: 'white',
      localConfig: {
        arrows: true,
        direction: 'ltr',
        drag: true,
        pagination: false,
        perPage: 1,
        speed: this.$timing('normal'),
        start: 0,
      },
      blocksData: this.blocks,
    }
  },
  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },
  mounted() {
    this.setResizeObserver()
    this.getStoreLocator()
  },
  methods: {

    /**
     * Fetch store data if store ids exist
     */
    async getStoreLocator() {
      try {
        const storeIds = this.blocks
          .filter((block) => block.uberallStoreId)
          .map((block) => block.uberallStoreId)

        if (!storeIds.length) {
          return
        }

        const requestedFields =
          '?fieldMask=city&' +
          'fieldMask=country&' +
          'fieldMask=id&' +
          'fieldMask=name&' +
          'fieldMask=nextOpen&' +
          'fieldMask=openingHours&' +
          'fieldMask=openNow&' +
          'fieldMask=photos&' +
          'fieldMask=province&' +
          'fieldMask=streetAndNumber&' +
          'fieldMask=zip&' +
          'fieldMask=shopify_store_page_url'
        const promises = storeIds
          .map((storeId) => fetch(`${storeLocatorProps.activeEnvironmentUrl}${storeId}${requestedFields}`)
            .then(response => response.json()))
        const stores = await Promise.all(promises)

        this.addStoreDetailsToData(stores)
      } catch (error) {
        console.error('Error fetching store data:', error)
      }
    },

    /**
     * Add store data to block
     * @param stores
     */
    addStoreDetailsToData(stores) {
      stores.forEach((storeData) => {
        const storeResponse = storeData.response

        this.blocksData = this.blocksData.map((block) => {
          if (block.uberallStoreId === storeResponse.id) {
            return {
              ...block,
              ...this.refinedStoreData(storeResponse),
            }
          }

          return block
        })
      })
    },

    /**
     * Refine store data passed to blovk
     * @param storeResponse
     */
    refinedStoreData(storeResponse) {
      const storeCopy = `${storeResponse.streetAndNumber}<br>${storeResponse.city}, ${storeResponse.province} ${storeResponse.zip}`
      const hoursString = getStoreHours(storeResponse)
      const refineStoreName = storeResponse?.name?.split('Veronica Beard')?.pop()?.trim()
      const ctaObject = storeResponse?.customFields?.find((field) => field.name === 'shopify_store_page_url')

      return {
        openNow: storeResponse.openNow,
        openHours: hoursString,
        heading: refineStoreName,
        copy: storeCopy,
        mobileCopy: storeCopy,
        ctaUrl: ctaObject?.value,
      }
    },

    /**
     * Updates tab underline on mouse and focus events
     * @param {Number} idx - Tab index.
     * @param {String} evt - Event type.
     */
    handleSplideEvent(idx, evt) {
      if (evt === 'out') {
        this.updateSlider(this.$refs.splide, this.$refs.splide.splide.index)
      } else {
        this.updateSlider(this.$refs.splide, idx, false)
      }
    },

    /**
     * Updates tab underline on mounted event
     * @param {Object} _ - Splide instance.
     */
    handleSplideMounted(_) {
      this.updateSlider(_, 0)
    },

    /**
     * Updates tab underline on slide moved event
     * @param {Object} _ - Splide instance.
     * @param {Number} newIndex - New slide index.
     */
    handleSplideMove(_, newIndex) {
      this.updateSlider(_, newIndex)
    },

    /**
     * Updates tab underline position and width
     */
    updateSlider(_, newIndex, updateColor = true) {
      const tabUnderline = this.$refs.tab_underline
      const tab = this.$refs.splide__arrows.children[newIndex]
      tabUnderline.style.width = `${tab.offsetWidth}px`
      tabUnderline.style.left = `${tab.offsetLeft}px`
      if (updateColor) {
        this.colorScheme = this.blocks[newIndex].colorScheme
      }
    },

    /**
     * Sets up resize observer to update slider on resize
     */
    setResizeObserver() {
      const self = this
      const slider = this.$refs.slider
      const observer = new ResizeObserver(() => {
        self.updateSlider(self.$refs.splide, self.$refs.splide.splide.index)
      })
      observer.observe(slider)
    },

    setBlockClasses(block) {
      const classes = {}

      if (!block) {
        return classes
      }

      classes[`image-text-overlay--desktop-${block.desktopImageHeightClass}`] = Boolean(block.desktopImageHeightClass)
      classes[`image-text-overlay--mobile-${block.mobileImageHeightClass}`] = Boolean(block.mobileImageHeightClass)
      return classes
    },

    /**
     * Sets up inline styles for blocks
     * @param {Object} block - Block data.
     */
    setInlineStyles(block) {
      const styles = {
        '--desktop-image': `url(${block.desktopImage})`,
        '--desktop-image-height': `${block.desktopImageHeight}px`,
        '--desktop-text-align': block.desktopTextAlign,
        '--desktop-text-color': block.desktopTextColor,
        '--desktop-text-left-padding': `${block.desktopTextLeftPadding}px`,
        '--desktop-text-right-padding': `${block.desktopTextRightPadding}px`,
        '--margin-top-desktop': `${block.desktopMarginTop}px`,
        '--margin-bottom-desktop': `${block.desktopMarginBottom}px`,
        '--mobile-image': `url(${block.mobileImage})`,
        '--mobile-image-height': `${block.mobileImageHeight}px`,
        '--mobile-text-align': block.mobileTextAlign,
        '--mobile-text-color': block.mobileTextColor,
        '--mobile-text-bottom-padding': `${block.mobileTextBottomPadding}px`,
        '--mobile-text-left-padding': `${block.mobileTextLeftPadding}px`,
        '--mobile-text-right-padding': `${block.mobileTextRightPadding}px`,
        '--mobile-text-top-padding': `${block.mobileTextTopPadding}px`,
        '--margin-top-mobile': `${block.mobileMarginTop}px`,
        '--margin-bottom-mobile': `${block.mobileMarginBottom}px`,
      }

      if (block.desktopEnableGradient) {
        styles.push('--desktop-gradient-start', block.desktopGradient)
      }

      if (block.mobileEnableGradient) {
        styles.push('--mobile-gradient-start', block.mobileGradient)
      }

      return styles
    },
  },
}
</script>

<style lang="scss">
  @import './image-text-overlay';
</style>
